<script>
import DateTimePickerControl from '@components/DateTimePickerControl'
import Dates from '@constants/Dates'
import InputMixin from '@mixins/InputMixin'
import { formatDate, toISOString, tryParseDate } from '@helpers/DateHelper'
import { isArray } from 'lodash'

// Public: Allows selecting multiple date ranges. Each range consists of a start
// and end date. Single dates are supported (startDate and endDate are the same).
//
// The value is an array of objects:
//   [{ startDate: '2026-06-01', endDate: '2026-06-04' }, { startDate: '2026-06-07', endDate: '2026-06-07' }]
export default {
  name: 'DateMultiRangePicker',
  components: {
    DateTimePickerControl,
  },
  mixins: [InputMixin],
  props: {
    // Public: An array of { startDate, endDate } objects with ISO date strings (YYYY-MM-DD).
    // For single dates, startDate and endDate should be the same value.
    value: { type: Array, default: () => [] },
  },
  data () {
    return {
      // Internal: Index of the range currently being edited, null when adding new.
      editingIndex: null,

      // Internal: Current date selection in the calendar picker.
      currentSelection: null,
    }
  },
  computed: {
    // Internal: Builds display-friendly objects from the value.
    displayRanges () {
      return (this.value || []).map(range => {
        const isSingleDate = range.startDate === range.endDate
        const startLabel = formatDate(range.startDate, { format: 'date' })
        const endLabel = formatDate(range.endDate, { format: 'date' })
        return {
          startDate: range.startDate,
          endDate: range.endDate,
          label: isSingleDate ? startLabel : `${startLabel}${Dates.RANGE_SEPARATOR}${endLabel}`,
        }
      })
    },
  },
  methods: {
    // Public: Opens the calendar to add a new date range.
    addRange () {
      this.editingIndex = null
      this.currentSelection = null
      this.$nextTick(() => this.$refs.picker.showCalendar())
    },
    // Public: Opens the calendar to edit an existing date range.
    editRange (index) {
      const range = this.value[index]
      this.editingIndex = index
      const startDate = tryParseDate(range.startDate, { format: 'date' })
      const endDate = tryParseDate(range.endDate, { format: 'date' })
      this.currentSelection = [startDate, endDate]
      this.$nextTick(() => this.$refs.picker.showCalendar())
    },
    // Public: Removes the date range at the specified index.
    removeRange (index) {
      const newValue = [...(this.value || [])]
      newValue.splice(index, 1)
      this.$emit('input', newValue)
    },
    // Internal: Handles calendar date range selection, adding or updating a range.
    onRangeSelected (dateRange) {
      if (!dateRange || !isArray(dateRange) || dateRange.length < 2) return

      const [start, end] = dateRange
      const startDate = toISOString(start, { dateOnly: true })
      const endDate = toISOString(end, { dateOnly: true })

      const rangeObj = { startDate, endDate }
      const newValue = [...(this.value || [])]
      if (this.editingIndex !== null) {
        newValue[this.editingIndex] = rangeObj
      } else {
        newValue.push(rangeObj)
      }

      this.editingIndex = null
      this.currentSelection = null
      this.$emit('input', newValue)
      this.$nextTick(() => this.$refs.picker.$refs.pickr.fp.clear())
    },
  },
}
</script>

<template>
  <div class="datetime-multi-range-picker">
    <Label v-if="label" :required="required" :hint="hint">{{ label }}</Label>

    <div v-if="displayRanges.length" class="datetime-multi-range-picker__ranges">
      <div v-for="(range, index) in displayRanges" :key="index" class="datetime-multi-range-picker__range-item">
        <span class="datetime-multi-range-picker__range-label">{{ range.label }}</span>
        <Button
          v-if="!disabled"
          icon
          compact
          name="Edit"
          class="datetime-multi-range-picker__action"
          @click="editRange(index)"
        />
        <Button
          v-if="!disabled"
          icon
          compact
          name="Close"
          class="datetime-multi-range-picker__action"
          @click="removeRange(index)"
        />
      </div>
    </div>

    <div class="datetime-multi-range-picker__input">
      <DateTimePickerControl
        ref="picker"
        :value="currentSelection"
        type="date_range"
        :placeholder="placeholder || 'Select date range'"
        :disabled="disabled"
        @input="onRangeSelected"
      />
    </div>

    <ActionLink
      v-if="!disabled"
      label="Add"
      class="datetime-multi-range-picker__add"
      @click="addRange"
    />

    <InputError v-if="error" :error="error"/>
  </div>
</template>

<style lang="scss" scoped>
.datetime-multi-range-picker {
  position: relative;

  ::v-deep {
    @include input;
  }
}

.datetime-multi-range-picker__ranges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.datetime-multi-range-picker__range-item {
  align-items: center;
  border: $br-light;
  border-radius: 4px;
  display: inline-flex;
  padding: 4px 8px;
}

.datetime-multi-range-picker__range-label {
  color: $fc-html;
  font-size: 14px;
  margin-right: 4px;
}

.datetime-multi-range-picker__action {
  margin-left: 2px;
}

.datetime-multi-range-picker__input {
  margin-bottom: 4px;
  position: relative;
}

.datetime-multi-range-picker__add {
  display: block;
  text-align: right;
}
</style>
